import { CurrencyPipe, DatePipe } from "@angular/common";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";
import { of, throwError } from "rxjs";
import { catchError, exhaustMap, map, switchMap } from "rxjs/operators";
import { selectClassifications, selectInfo, selectItStandingAuth } from "../../account-info-header/reducers";
import { RemoveHyphenFromAccountPipe } from "../../shared/pipes/remove-hyphen-from-account.pipe";
import { TableColumn } from "../../shared/types/TableColumn";
import { InternalTransferActions, InternalTransfersActions, TransactionFormActions } from "../actions";
import { InternalTransferInputResponse } from "../models/internal-transfer/InternalTransferInputResponse";
import { InternalTransferPositionsResponse } from "../models/internal-transfer/InternalTransferPositionsResponse";
import { ServiceError, ServiceErrorWithList } from "../models/shared/ServiceError";
import { selectCurrentTransaction, selectEditFLowFromTableComplete, selectInternalTransferInfo, selectInternalTransfersInfo, selectPositionLotTableData, selectPtrsInfo, selectPtrsInfoPaidFor, selectStagedTransactionForm, selectTransactionForm } from "../reducers";
import { InternalTransferService } from "../services/internal-transfer.service";
import { CodeAndDescription } from "../models/shared/CodeAndDescription";
import { InternalTransferListVerifyRequest, InternalTransferVerifyRequest } from "../models/internal-transfer/InternalTransferVerifyRequest";
import { Attributes } from "../../shared/models/JsonApiWrap";
import { ContributionReason } from "../models/internal-transfer/ContributionReason";
import { ItExtraPrompts, ItForm, ItToAccountDetails, ItTransactionDetails, TransactionForm, isInternalTransferForm } from "../models/form/TransactionForm";
import { InternalTransfersInfo } from "../models/internal-transfer/InternalTransfersInfo";
import { AccountInfoState } from "../../account-info-header/models/AccountInfoState";
import { DistributionReason } from "../models/internal-transfer/DistributionReason";
import { InternalTransferListVerifyResponse } from "../models/internal-transfer/InternalTransferVerifyResponse";
import { selectAcknowledgementDateTime, selectEAuthInfo } from "../../shared/reducers";
import { Classifications } from "../../account-info-header/models";
import { StandingInstruction } from "../../shared/types/StandingInstruction";
import { EAuthInfo } from "../../shared/models/EAuthInfo";
import { PtrsInfo } from "../models";
import { TransactionsTableColumn } from "../../shared/types/TransactionTableColumn";
import { InternalTransferAcknowledgeListRequest, InternalTransferAcknowledgeRequest } from "../models/internal-transfer/InternalTransferAcknowledgeRequest";
import { InternalTransferListAcknowledgeResponse } from "../models/internal-transfer/InternalTransferAcknowledgeResponse";
import { Constants, RegTypeCode, transactionCodes, yesNo } from "../../shared/utilities/Constants";
import { PaidForDetails, TrustFieldsRequestInfo } from "../../shared/models/TrustFieldsRequestInfo";
import { TransAddTrustFields } from "../../shared/models/TransAddTrustFields";
import { individual, PaidFor } from "../models/ptrs/PaidFor";
import { selectInitReleaseTransactionInContext } from "../../initiate-and-release/reducers";
import { DraftListAttributes } from "../../initiate-and-release/Models/Drafts/DraftResponse";
import { mapCustomerListSloa } from "./internal-transfer.effects";
@Injectable()
export class InternalTransfersEffects {
    hyphenPipe = new RemoveHyphenFromAccountPipe();

    constructor(
        private actions$: Actions,
        private internalTransferService: InternalTransferService,
        private store: Store,
        private currencyPipe: CurrencyPipe,
        private datepipe: DatePipe
    ) { }

    loadToAccounts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalTransfersActions.itToAccountEntered),
            switchMap((action) => {
                return this.internalTransferService.callInternalTransferInput(action.payload).pipe(
                    map((internalTransferInputResponse: InternalTransferInputResponse) => {
                        //Temporary change... will be removed after ATP returns invalid reg types
                        let regType: string = internalTransferInputResponse?.toAccountInfo?.registrationType ?? '';
                        let is401kToAccount: boolean = regType === RegTypeCode.ROTH_401K || regType === RegTypeCode.INDIVIDUAL_401K;
                        const errorText = internalTransferInputResponse.errorText ?? '';

                        if ((regType === '' || errorText?.toUpperCase() === Constants.toAccountEightDigitMsg.toUpperCase() 
                            || errorText?.toUpperCase() === Constants.toAccountWrongDigitsMsg.toUpperCase()) // checking condition for invalid account number, checking for either error message to decouple change from change to journals-api
                            || (is401kToAccount && errorText.trim().length > 0 && errorText?.trim().toUpperCase() != Constants.recipentError.toUpperCase())
                        ) {
                            internalTransferInputResponse.toAccountInfo.accountNumber = action.toAccount
                            return InternalTransfersActions.itLoadToAccountFinishedWithError({ internalTransferInputResponse });
                        }
                        return InternalTransfersActions.itLoadToAccountSuccess({ internalTransferInputResponse });
                    }),
                    catchError((err: ServiceError) =>
                        of(InternalTransferActions.loadToAccountFail(err))
                    )
                )
            })
        )
    );

    callPositions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                InternalTransfersActions.itLoadPositions,
                InternalTransferActions.loadPositions
            ),
            concatLatestFrom(() => [
                this.store.select(selectInfo),
                this.store.select(selectInternalTransferInfo),
                this.store.select(selectTransactionForm),
            ]),
            exhaustMap(([action, accountInfo, itInfo, transactionForm]) => {
                return this.internalTransferService.callInternalTransferPositions(accountInfo.accountId, this.hyphenPipe.transform(itInfo.toAccountInfo.accountNumber)).pipe(
                    map((internalTransferPositionsResponse: InternalTransferPositionsResponse) => {
                        const positions = internalTransferPositionsResponse?.positions;
                        if (positions !== null && positions !== undefined) {
                            positions.map(position => {
                                if (position?.costBasis !== null && position?.costBasis !== undefined) {
                                    position.costBasis = this.currencyPipe.transform(position?.costBasis)
                                }
                                if (position?.currentPricePerShare !== null && position?.currentPricePerShare !== undefined) {
                                    position.currentPricePerShare = this.currencyPipe.transform(position?.currentPricePerShare)
                                }
                                return position;
                            });
                            // Due to limitations for setting up test data for this exact scenario we are hardcoding it here 
                            // TODO: remove - hardcoded mock position for MF 30 day duplicate restriction after testing done 
                            const isAllowedEnvironment = ['dev01', 'sit01', 'qae01', 'localhost'].some(env => window.location.hostname.includes(env));
                            const isMockAllowed = isAllowedEnvironment && (
                                (accountInfo.accountId === '11536962' && accountInfo.userName === 'anjul_2023') ||
                                (accountInfo.accountId === '45719119' && accountInfo.userName === 'deepak_1065')
                            );
                            if (isMockAllowed) positions.push({
                                symbol: 'MFMOCK',
                                symbolName: 'MOCK MUTUAL FUND 30 DAY HOLD',
                                totalQuantity: 500.0,
                                numberOfLots: 1.0,
                                currentPricePerShare: "$25.00",
                                costBasis: '$125.75',
                                estimatedMarketValue: 12500.00,
                                isMutualFund: true,
                                setToAverage: false,
                                schwabSecurityId: 999999999,
                                cmtSequenceId: 99,
                                valMultiplier: 1.0,
                                quantity: { selectedQuantityMinimum: 1.0, selectedQuantityMinimumDenomination: 1.0 },
                                lots: [
                                    {
                                        acquiredDate: '01/01/2026',
                                        lotQuantity: 500.0,
                                        holdingPeriod: 'Short',
                                        costBasis: '$125.75',
                                        lotId: 'mock-lot-mf30-001',
                                        purchaseDate: '01/01/2026',
                                        purchasePrice: 25.00
                                    }
                                ],
                                restriction: {
                                    isRestricted: true,
                                    restrictionText: 'DUPLICATE: Contact service tm to review procedure for MF 30 day Margin.'
                                }
                            });
                        }
                        return InternalTransfersActions.itLoadPositionsSuccessful({ internalTransferPositionsResponse });
                    }),
                    catchError((err: ServiceError) =>
                        of(InternalTransfersActions.itLoadPositionsFail(err))
                    )
                )
            })
        )
    );

    callListVerify$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TransactionFormActions.callInternalTransferListVerify),
            concatLatestFrom(() => [
                this.store.select(selectTransactionForm),
                this.store.select(selectInternalTransfersInfo),
                this.store.select(selectInfo),
                this.store.select(selectPtrsInfoPaidFor),
                this.store.select(selectInitReleaseTransactionInContext),
                this.store.select(selectPositionLotTableData),
                this.store.select(selectItStandingAuth),
                this.store.select(selectInitReleaseTransactionInContext)
            ]),
            exhaustMap(([_action, transactionForm, itInfo, accountInfo, paidForInfo, initReleaseTransactionInContext, extraPrompts, instructions, InitReleaseTransactionInContext]) => {
                if (isInternalTransferForm(transactionForm)) {
                    let request = this.createListVerifyRequest(
                        transactionForm as ItForm,
                        itInfo,
                        accountInfo,
                        paidForInfo,
                        initReleaseTransactionInContext,
                        transactionForm.extraPrompts,
                        instructions,
                        InitReleaseTransactionInContext,
                        _action.isInRViewReviewFlow
                    );
                    return this.internalTransferService.callInternalTransferListVerify(request).pipe(
                        map((response: InternalTransferListVerifyResponse) => {
                            let error = false;
                            response?.data.forEach((toAccountResponse) => {
                                if (toAccountResponse.attributes?.hasError) {
                                    error = true;
                                }
                            })
                            return error ? InternalTransfersActions.verifyListCallFinishedWithError({ response }) : InternalTransfersActions.verifyListCallSuccessful({ response });
                        }),
                        catchError((err: ServiceError) =>
                            of(InternalTransfersActions.verifyListCallFailed(err))
                        )
                    )
                }
                return throwError(() => new Error('invalid form type for internal transfer list verify'));
            })
        )
    );

    callAcknowledgeList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InternalTransfersActions.callAcknowledgeList),
            concatLatestFrom(() => [
                this.store.select(selectInfo),
                this.store.select(selectInternalTransfersInfo),
                this.store.select(selectStagedTransactionForm),
                this.store.select(selectItStandingAuth),
                this.store.select(selectClassifications),
                this.store.select(selectPtrsInfo),
                this.store.select(selectAcknowledgementDateTime),
                this.store.select(selectPositionLotTableData),
                this.store.select(selectEAuthInfo),
                this.store.select(selectEditFLowFromTableComplete),
                this.store.select(selectCurrentTransaction),
                this.store.select(selectPtrsInfoPaidFor)
            ]),
            exhaustMap(([
                _action,
                accountInfo,
                itInfo,
                transactionFormState,
                standingInstructions,
                classifications,
                ptrsInfo,
                acknowledgementConsentDateTime,
                positionsInfo,
                eauthInfo,
                isEdit,
                currentTransaction,
                paidForInfo
            ]) => {
                let request = this.createAcknowledgeListRequest(
                    accountInfo,
                    itInfo,
                    transactionFormState.transactionDetails as ItTransactionDetails,
                    transactionFormState.extraPrompts as ItExtraPrompts,
                    standingInstructions,
                    eauthInfo,
                    classifications,
                    ptrsInfo,
                    acknowledgementConsentDateTime,
                    positionsInfo,
                    isEdit,
                    currentTransaction!,
                    paidForInfo
                );
                return this.internalTransferService.callInternalTransferListAcknowledge(request).pipe(
                    map((response: InternalTransferListAcknowledgeResponse) => {
                        return InternalTransfersActions.acknowledgeListCallSuccessful({ response });
                    }),
                    catchError((err: ServiceErrorWithList) =>
                        of(InternalTransfersActions.acknowledgeListCallFail(err))
                    )
                )
            })
        )
    );

    createListVerifyRequest(transactionForm: ItForm, itInfo: InternalTransfersInfo, accountInfo: AccountInfoState, paidForInfo: PaidFor, initReleaseTransactionInContext: DraftListAttributes, extraPrompts: ItExtraPrompts, instructions: StandingInstruction[], InitReleaseTransactionInContext: DraftListAttributes, isInRViewReviewFlow?: boolean): InternalTransferListVerifyRequest[] {
        const transactionDetails = transactionForm.transactionDetails as ItTransactionDetails;
        const isBsloaTrans = transactionForm.extraPrompts.useStandingAuth === 'bundled';
        const useSloa = isBsloaTrans ? transactionForm.extraPrompts.useBundledStandingAuth === 'yes' : transactionForm.extraPrompts.useStandingAuth === yesNo.yes;
        const standingInstruction = instructions.find(instruction => extraPrompts.standingAuthId == 'i-' + instruction.cashieringRqstId + '-' + instruction.cashieringRqstSecondId);

        const cashieringRequestId = (useSloa && InitReleaseTransactionInContext.draftId) ? Number(InitReleaseTransactionInContext.draftObject.cashieringRequestId) : Number(standingInstruction?.cashieringRqstId);
        let cashieringRequestSecondId = (useSloa && InitReleaseTransactionInContext.draftId) ? Number(InitReleaseTransactionInContext.draftObject.cashieringSecondRequestId) : Number(standingInstruction?.cashieringRqstSecondId);

        const blankIraContributionType: ContributionReason = {
            code: '',
            description: '',
            subCode: '',
            year: ''
        };
        const selectedCostBasisMethod: string = transactionDetails?.costBasisMethod ?? '';
        let data: Attributes<InternalTransferVerifyRequest>[] = [];
        transactionDetails?.toAccounts?.forEach(detail => {
            const toAccount: string = detail.toAccount;
            const isIraAccount = itInfo?.iraFields[toAccount]?.isIraTransaction;
            const distributionReason = isIraAccount ? itInfo?.iraFields[toAccount]?.distributionReasonMap?.filter(elem => elem.description === detail.iraDistributionType)[0] : new DistributionReason();
            const stateTaxPercent: string = isIraAccount &&
                !(distributionReason?.code?.trim() === 'Q' || distributionReason?.code?.trim() === '0') ?
                transactionDetails.taxWithholdingsFormGroup.stateTaxPercent.replace('%', '') : '0';
            const fedTaxPercent: string = isIraAccount &&
                !(distributionReason?.code?.trim() === 'Q' || distributionReason?.code?.trim() === '0') ?
                transactionDetails.taxWithholdingsFormGroup.fedTaxPercent.replace('%', '') : '0';
            const positionsInfo: TableColumn[] = itInfo?.rowData[toAccount]?.positionData;
            const hasCash: boolean = (!!transactionDetails?.cashAmounts?.length && transactionDetails?.cashAmounts?.length > 0) ?? false;
            const cashAmountsIndex: number = hasCash ? transactionDetails?.cashAmounts?.findIndex(detail => detail.toAccount === toAccount) ?? 0 : 0;
            let paidForContact: individual = (isInRViewReviewFlow ? initReleaseTransactionInContext.draftObject.paidFor : paidForInfo.individuals.find(individual => (transactionDetails?.trustPaidFor?.toLocaleLowerCase().includes(individual.name.toLocaleLowerCase())) && (transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.role.toLocaleLowerCase()))) ?? new PaidForDetails()) ?? new PaidForDetails();
            let request: Attributes<InternalTransferVerifyRequest> = {
                id: '',
                type: 'journal-verify-request',
                attributes: {
                    draftId: initReleaseTransactionInContext?.draftId ?? '',
                    bsloaTransaction: isBsloaTrans,
                    stateTaxAttentionRequired: false,
                    amount: (!!transactionDetails?.cashAmounts?.length && transactionDetails?.cashAmounts?.length > 0) ? transactionDetails?.cashAmounts[cashAmountsIndex]?.amount?.replace('$', '').replace(',', '') ?? '0' : '0',
                    amountType: (!!transactionDetails?.cashAmounts?.length && transactionDetails?.cashAmounts?.length > 0) ? transactionDetails?.cashAmounts[cashAmountsIndex]?.amountType ?? '' : '',
                    processDate: this.datepipe.transform(transactionDetails.processDate, 'yyyy-MM-dd') ?? '',
                    isIraTransaction: isIraAccount,
                    isWithholdFedTax: Number.parseFloat(fedTaxPercent) !== 0,
                    fedTaxAmount: fedTaxPercent,
                    isWithholdStateTax: Number.parseFloat(stateTaxPercent) !== 0,
                    stateTaxAmount: stateTaxPercent,
                    isSloaPopupSelected: useSloa,
                    isCreateSloa: false,
                    nickname: '',
                    isRecurringTransaction: false,
                    process2ndDate: '',
                    endDate: '',
                    paidFor: paidForContact,
                    trustIncmPct: itInfo.trustFields[toAccount].trustIncmPct ?? '',
                    trustIncmToPct: '',
                    trustPrinPct: itInfo.trustFields[toAccount].trustPrinPct ?? '',
                    trustPrinToPct: '',
                    trustTaxCode: transactionDetails.trustTaxCode ?? '',
                    trustDepositCode: '',
                    trustTaxCategory: transactionDetails.trustTaxCategory ?? '',
                    depositTaxCode: detail.trustDepositCode ?? '',
                    toIncome: (parseFloat(detail.trustIncomeToPercent!)).toString(),
                    toPrincipal: (parseFloat(detail.trustPrincipalToPercent!)).toString(),
                    selectedCostBasisMethod: selectedCostBasisMethod,
                    defaultCostBasisMethod: itInfo?.positionsLotDetails?.costMethod,
                    selectedPositionsCount: this.getSelectedPositionCount(positionsInfo),
                    selectedLotsCount: selectedCostBasisMethod === 'None' ? this.getSelectedlotCount(positionsInfo) : 0,
                    selectedPositionsList: this.createSelectedPositionsList(positionsInfo, isInRViewReviewFlow),
                    isLoaOnFile: false,
                    isEdit: false,
                    cashieringNoteText: "",
                    stockHandlerId: itInfo?.positionsInfo?.cmtRequestId !== null ? itInfo?.positionsInfo?.cmtRequestId : 0,
                    tranMemo: transactionDetails.trustComment ?? '',
                    distributionReason: distributionReason,
                    payIncome: new CodeAndDescription(),
                    frequency: new CodeAndDescription(),
                    iraContributionType: itInfo?.toAccountPropertiesById[toAccount]?.iraContributionTypeMap?.filter(elem => elem.description === detail?.iraContributionType)[0] ?? blankIraContributionType,
                    toAccountId: toAccount.replace('-', ''),
                    cashieringRequestId: cashieringRequestId,
                    cashieringSecondRequestId: cashieringRequestSecondId,
                    masterAccountId: accountInfo?.masterAccount,
                    subAccountId: accountInfo?.accountId,
                    stateCd: itInfo.stateCd
                }
            }
            data.push(request);
        });
        return data;
    }

    createAcknowledgeListRequest(
        accountInfo: AccountInfoState,
        itInfo: InternalTransfersInfo,
        transactionDetails: ItTransactionDetails,
        extraPrompts: ItExtraPrompts,
        instructions: StandingInstruction[],
        eAuthInfo: EAuthInfo,
        classifications: Classifications,
        ptrsInfo: PtrsInfo,
        acknowledgementConsentDateTime: string,
        positionsInfo: TableColumn[],
        isEdit: boolean,
        currentTransaction: TransactionsTableColumn,
        paidForInfo: PaidFor
    ): InternalTransferAcknowledgeListRequest[] {
        const blankIraContributionType: ContributionReason = {
            code: '',
            description: '',
            subCode: '',
            year: ''
        }
        let data: Attributes<InternalTransferAcknowledgeRequest>[] = [];
        transactionDetails?.toAccounts?.forEach((detail, index) => {
            const toAccount: string = detail.toAccount;
            const verifyResponse = itInfo.multipleToJournalsList.find(verifyResponse => this.hyphenPipe.transform(verifyResponse.toAccountInfo.accountNumber) === this.hyphenPipe.transform(detail.toAccount));
            const isIraAccount = itInfo?.iraFields[toAccount].isIraTransaction;
            const isBsloaTrans = extraPrompts.useStandingAuth === 'bundled';
            const useSloa = isBsloaTrans ? extraPrompts.useBundledStandingAuth === 'yes' : extraPrompts.useStandingAuth === yesNo.yes;
            const standingInstruction = instructions.find((instruction) =>
                extraPrompts.standingAuthId ==
                'i-' +
                instruction.cashieringRqstId +
                '-' +
                instruction.cashieringRqstSecondId
            );
            const cashInputFields = transactionDetails?.cashAmounts?.find(cashField => cashField.toAccount === toAccount);
            const distributionReason = itInfo?.iraFields[toAccount].isIraTransaction ?
                itInfo?.iraFields[toAccount].distributionReasonMap.filter(elem => elem.description === detail.iraDistributionType)[0] : new DistributionReason();
            const stateTaxPercent: string = itInfo.iraFields[toAccount].isIraTransaction &&
                !(distributionReason?.code?.trim() === 'Q' || distributionReason?.code?.trim() === '0') ?
                transactionDetails.taxWithholdingsFormGroup.stateTaxPercent.replace('%', '') : '0';
            const fedTaxPercent: string = itInfo.iraFields[toAccount]?.isIraTransaction &&
                !(distributionReason?.code?.trim() === 'Q' || distributionReason?.code?.trim() === '0') ?
                transactionDetails.taxWithholdingsFormGroup.fedTaxPercent.replace('%', '') : '0';
            const selectedCostBasisMethod: string = transactionDetails?.costBasisMethod ?? '';

            positionsInfo = itInfo.rowData[toAccount].positionData;
            const cashieringRequestId: number = isEdit
                ? Number.parseInt(currentTransaction.CashieringRqstId!)
                : standingInstruction?.cashieringRqstId
                    ? Number.parseInt(standingInstruction!.cashieringRqstId)
                    : 0;
            const cashieringRequestSecondId: number = isEdit
                ? Number.parseInt(currentTransaction.CashieringRqstSecondId!)
                : standingInstruction?.cashieringRqstSecondId
                    ? Number.parseInt(standingInstruction!.cashieringRqstSecondId)
                    : 0;
            let selectedEauthCustomer = eAuthInfo.isAccountHolderSelected
                ? verifyResponse?.eAuthModel.custAcctAuthorizationsList.eAuthCustomerList.filter(
                    (customer) =>
                        customer.customerId === eAuthInfo.selectedAccountHolder[0]
                ) ?? [] : [];
            let request: Attributes<InternalTransferAcknowledgeRequest> = {
                id: '',
                attributes: {
                    stateCd: itInfo.stateCd,
                    masterAccountId: this.hyphenPipe.transform(accountInfo?.masterAccount),
                    subAccountId: this.hyphenPipe.transform(accountInfo?.accountId),
                    toAccountId: this.hyphenPipe.transform(itInfo?.toAccountInfo[toAccount]?.accountNumber),
                    bsloaTransaction: extraPrompts.useStandingAuth === 'bundled',
                    stateTaxAttentionRequired: false,
                    amount: extraPrompts.transferType !== 'positions' ?
                        cashInputFields?.amount.replace('$', '') ?? '0'
                        : '0',
                    amountType: cashInputFields?.amountType ?? '',
                    processDate: verifyResponse?.processDate ?? '',
                    isIraTransaction: isIraAccount,
                    fedTaxAmount: fedTaxPercent,
                    isWithholdFedTax: Number.parseFloat(fedTaxPercent) !== 0,
                    eauthSelected: (verifyResponse?.clientAuthorizationNeeded ?? false) && eAuthInfo.isAccountHolderSelected,
                    isSubmitAndCreateSLOA: itInfo.isSubmitAndCreate,
                    customerList: selectedEauthCustomer,
                    customerListSloa: mapCustomerListSloa(itInfo.isSubmitAndCreate, selectedEauthCustomer, verifyResponse?.eAuthModel),
                    stockHandlerId:
                        verifyResponse?.positionsInfo
                            .cmtRequestId !== null
                            ? verifyResponse?.positionsInfo
                                .cmtRequestId ?? 0
                            : 0,
                    isUseSloa: this.isSloaSelected(extraPrompts),
                    trustFieldsRequestInfo: this.mapAddTransFields(
                        classifications,
                        toAccount,
                        transactionDetails,
                        ptrsInfo,
                        itInfo
                    ),
                    isWithholdStateTax: Number.parseFloat(stateTaxPercent) !== 0,
                    stateTaxAmount: stateTaxPercent,
                    isSloaPopupSelected: useSloa,
                    isCreateSloa: false,
                    nickname: '',
                    isRecurringTransaction: itInfo.recurringFields[toAccount]?.isRecurringTransaction,
                    process2ndDate: itInfo.recurringFields[toAccount]?.secondProcessDate,
                    endDate: itInfo.recurringFields[toAccount]?.endDate,
                    paidFor: paidForInfo.individuals.find(individual => (transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.name.toLocaleLowerCase())) &&
                        (transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.role.toLocaleLowerCase()))) ?? new PaidForDetails(),
                    trustIncmPct: itInfo.trustFields[toAccount]?.trustIncmPct,
                    trustIncmToPct: itInfo.toAccountInfo[toAccount]?.toIncome,
                    trustPrinPct: itInfo.trustFields[toAccount]?.trustPrinPct,
                    trustPrinToPct: itInfo.toAccountInfo[toAccount]?.toPrincipal,
                    trustTaxCode: itInfo.trustFields[toAccount]?.trustTaxCode,
                    trustTaxCategory: transactionDetails.trustTaxCategory ?? '',
                    trustDepositCode: itInfo.trustFields[toAccount]?.trustDepositCode,
                    depositTaxCode: transactionDetails.trustDepositCode ?? '',
                    toIncome: itInfo.toAccountInfo[toAccount]?.toIncome ?? '',
                    toPrincipal: itInfo.toAccountInfo[toAccount]?.toPrincipal ?? '',
                    selectedCostBasisMethod: selectedCostBasisMethod,
                    defaultCostBasisMethod: itInfo?.positionsLotDetails?.costMethod,
                    selectedPositionsCount: this.getSelectedPositionCount(positionsInfo),
                    selectedLotsCount:
                        selectedCostBasisMethod === 'None'
                            ? this.getSelectedlotCount(positionsInfo)
                            : 0,
                    selectedPositionsList: this.createSelectedPositionsList(positionsInfo),
                    isLoaOnFile: false,
                    isEdit: isEdit,
                    cashieringNoteText: '',
                    tranMemo: '',
                    distributionReason: distributionReason,
                    payIncome: itInfo.recurringFields[toAccount]?.payIncome,
                    frequency: itInfo.recurringFields[toAccount]?.frequency,
                    iraContributionType:
                        itInfo?.toAccountPropertiesById[
                            toAccount
                        ].iraContributionTypeMap?.filter(
                            (elem) => elem.description === detail?.iraContributionType
                        )[0] ?? blankIraContributionType,
                    acknowledgementConsentDateTime: acknowledgementConsentDateTime,
                    cashieringRequestId: cashieringRequestId,
                    cashieringSecondRequestId: cashieringRequestSecondId,
                    draftId: itInfo.journalAuthResponse[this.hyphenPipe.transform(toAccount)]?.draftId,
                    isThirdParty: itInfo?.toAccountPropertiesById[toAccount].isThirdParty ?? false
                },
                type: 'journal-acknowledge-request',
            };
            data.push(request);
        });
        return data;
    }

    getSelectedPositionCount(positions: TableColumn[]) {
        let positionsChanged = 0;
        for (var i = 0; i < positions?.length; i++) {
            if (!!positions[i]['Shares to move']) {
                positionsChanged++;
            }
        }
        return positionsChanged;
    }

    getSelectedlotCount(positions: TableColumn[]) {
        let lotCount = 0;
        positions.forEach(function (position: TableColumn) {
            position['lotDetail'].forEach(function (lot: TableColumn) {
                if (!!lot['lotsToMove']) {
                    lotCount++;
                }
            });
        });
        return lotCount;
    }

    getLotDetails(position: TableColumn, isInRViewReviewFlow: Boolean = false) {
        let lots: Array<any> = [];
        lots = position.map((lot: TableColumn) => {
            if (!!lot['lotsToMove']) {
                return {
                    lotId: lot['lotId'],
                    dateAcquired: lot['acquiredDate'],
                    holdingPeriodCode: isInRViewReviewFlow ? lot['holdingPeriod'] : this.getHoldingPeriodCode(lot['holdingPeriod']),
                    selectedQuantity: lot['lotsToMove'],
                    purchasePrice: lot['purchasePrice'],
                    purchaseDate: lot['purchaseDate'],
                    quantity: lot['lotQuantity'],
                    costBasis: lot['costBasis'],
                    marketValue: parseFloat(lot["estimatedMarketValue"].toString().replace(/[\$,]/g, '')),
                }
            }
        });
        lots = lots.filter(Boolean);
        return lots;
    }

    getHoldingPeriodCode(holdingPeriod: string): string {
        switch (holdingPeriod) {
            case 'Short':
                return 'S';
            case 'Long':
                return 'L';
            default:
                return '';
        }
    }

    mapAddTransFields(classifications: Classifications, toAccount: string, transactionDetails: ItTransactionDetails, ptrsInfo: PtrsInfo, itInfo: InternalTransfersInfo): TransAddTrustFields | null {
        let fromTrustFields: TrustFieldsRequestInfo | null = null;
        let toTrustFields: TrustFieldsRequestInfo | null = null;

        if (classifications.isPtrs) {
            const selectedContact: PaidForDetails = ptrsInfo.paidFor.individuals.find(individual => (transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.name.toLocaleLowerCase())) && (transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.role.toLocaleLowerCase()))) ?? new PaidForDetails();

            fromTrustFields = {
                accountNumber: this.hyphenPipe.transform(itInfo.accountInfoBanner.subAccountNumber),
                incomePercent: parseFloat(transactionDetails.trustIncomePercent!),
                principalPercent: parseFloat(transactionDetails.trustPrincipalPercent!),
                paidForContact: selectedContact,
                settlementDate: this.datepipe.transform(transactionDetails.processDate, 'yyyy-MM-dd') ?? '',
                tradeDate: new Date().toISOString(),
                taxCode: transactionDetails.trustTaxCode!,
                comment: transactionDetails.trustComment!,
                transactionCode: transactionCodes.internalTransferFromPtrs
            };
        }

        let isTrustNonPTRSAccount = itInfo.toAccountPropertiesById[toAccount].isTrustNonPTRSAccount;
        if (itInfo.toAccountInfo[toAccount]?.isTrust && !isTrustNonPTRSAccount) {
            const defaultContact: PaidForDetails = {
                id: -1,
                name: 'Paid For The Trust',
                role: 'Owner'
            };
            let toTransactionDetails = this.getToTrustFields(transactionDetails, toAccount);
            toTrustFields = {
                accountNumber: this.hyphenPipe.transform(itInfo.toAccountInfo[toAccount].accountNumber),
                incomePercent: parseFloat(toTransactionDetails.trustIncomeToPercent!),
                principalPercent: parseFloat(toTransactionDetails.trustPrincipalToPercent!),
                paidForContact: defaultContact,
                settlementDate: this.datepipe.transform(transactionDetails.processDate, 'yyyy-MM-dd') ?? '',
                tradeDate: new Date().toISOString(),
                taxCode: toTransactionDetails.trustDepositCode!,
                comment: transactionDetails.trustComment!,
                transactionCode: transactionCodes.internalTransferToPtrs
            };
        }
        return { fromTrustFields, toTrustFields };
    }

    getToTrustFields(transactionDetails: ItTransactionDetails, toAccount: string): ItToAccountDetails {
        let response = <ItToAccountDetails>{};
        if (transactionDetails?.toAccounts) {
            transactionDetails?.toAccounts.forEach((acc: ItToAccountDetails) => {
                if (acc?.toAccount === toAccount) {
                    response = acc;
                }
            })
        }
        return response;
    }

    createSelectedPositionsList(positions: TableColumn[], isInRViewReviewFlow?: Boolean) {
        let lotDetail: Array<any> = [];
        lotDetail = positions?.map((position: any) => {
            if (position['Shares to move'] !== '') {
                let quantity = parseFloat(position["Available shares"].split(' ')[0].replace(/[\$,]/g, ''));
                let price = parseFloat(position["Current price/share"].toString().replace(/[\$,]/g, ''));
                let selectedMarketValue = (price * position['Shares to move'] * Number(position['valMultiplier'])).toFixed(2);
                return {
                    "selectedQuantity": position['Shares to move'], //sending positive selectedQuantity even for short positions since masking is done in backend now
                    "schwabSecurityId": position['schwabSecurityId'],
                    "symbol": position['Symbol'],
                    "fullName": position['Name'],
                    "cmtSequenceId": position['cmtSequenceId'],
                    "multiplier": position['valMultiplier'],
                    "costBasis": position['Cost basis'],
                    "lots": this.getLotDetails(position['lotDetail'], isInRViewReviewFlow),
                    "quantity": quantity,
                    "price": price,
                    "selectedMarketValue": selectedMarketValue,
                    "numberOfLots": position['lotDetail'].length
                }
            }
        })
        lotDetail = lotDetail?.filter(Boolean);
        return lotDetail;
    }

    isSloaSelected(extraPrompts: ItExtraPrompts): boolean {
        const isBundledtransaction = extraPrompts.useStandingAuth === 'bundled';
        return isBundledtransaction ? extraPrompts.useBundledStandingAuth === 'yes' : extraPrompts.useStandingAuth === 'yes';
    }
}
